import React, { startTransition, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LoadingSkeleton, Tooltip } from '@reapitform/common-ui'
import { InfoIcon } from '@reapit/elements/icons/info'
import { Text } from '@reapit/elements/utils/text'
import { FormTypeCard, FormTypeTileItem } from './form-type-card'
import { CardsRow, ChipsRow, TilesContainer, TitleContainer, TypeChip } from './style'
import { getFormFamily, useAdminEnums } from '../../../../api/enums'
import { useAdminFormsList } from '../../../../api/forms'

/** URL param for the family tier (top row). Separate from `filterByType` (the sub-type tier, and also the
 * server's own `formType` query param) so the two tiers can be read and cleared independently. */
export const FAMILY_FILTER_PARAM = 'filterByFamily'

/**
 * Two-tier quick filters: one tile per document family (Agreement/Notice) with a live count, then — once a
 * family is selected — a row of lighter sub-type chips for the `FormType`s within it. Selecting a family
 * clears any sub-type filter left over from a different family; selecting "All Forms" clears both tiers.
 */
export const FormTypeTiles: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: enums, loading: enumsLoading } = useAdminEnums()
  const { data: forms, loading: formsLoading } = useAdminFormsList()

  const activeFamily = searchParams.get(FAMILY_FILTER_PARAM)
  const activeType = searchParams.get('filterByType')
  const showSkeleton = enumsLoading || formsLoading

  // The list is now one row per form VERSION (a form with 3 versions appears 3 times) — these tiles count
  // actual forms, so dedupe by referenceID before counting.
  const distinctForms = useMemo(() => {
    const seen = new Set<string>()
    return (forms ?? []).filter((form) => {
      if (seen.has(form.referenceID)) return false
      seen.add(form.referenceID)
      return true
    })
  }, [forms])

  const familyTiles: FormTypeTileItem[] = useMemo(() => {
    const families = Array.from(
      new Set((enums?.FormType ?? []).map((option) => option.family).filter((value): value is string => !!value)),
    )
    const tiles = families.map((familyValue) => ({
      value: familyValue,
      label: familyValue === 'Notice' ? 'Notices' : `${familyValue}s`,
      count: distinctForms.filter((form) => getFormFamily(enums, form.formType) === familyValue).length,
    }))
    // Leading "All Forms" tile clears both tiers and shows the total count.
    return [{ value: '', label: 'All Forms', count: distinctForms.length }, ...tiles]
  }, [enums, distinctForms])

  // Sub-type chips for the selected family only — empty (and hidden) until a family tile is active.
  const typeChips: FormTypeTileItem[] = useMemo(() => {
    if (!activeFamily) return []
    return (enums?.FormType ?? [])
      .filter((option) => option.family === activeFamily)
      .map((option) => ({
        value: option.value,
        label: option.shortName || option.label,
        count: distinctForms.filter((form) => form.formType === option.value).length,
      }))
  }, [enums, activeFamily, distinctForms])

  const handleSelectFamily = (value: string | null) => {
    startTransition(() => {
      setSearchParams((params) => {
        if (!value) params.delete(FAMILY_FILTER_PARAM)
        else params.set(FAMILY_FILTER_PARAM, value)
        // A sub-type filter from a different family no longer applies once the family changes.
        params.delete('filterByType')
        return params
      })
    })
  }

  const handleSelectType = (value: string | null) => {
    startTransition(() => {
      setSearchParams((params) => {
        if (!value) params.delete('filterByType')
        else params.set('filterByType', value)
        return params
      })
    })
  }

  return (
    <TilesContainer>
      <TitleContainer>
        <Text font="text-sm/bold" colour="primary">
          Quick Filters
        </Text>
        <Tooltip tooltipContent="Select a card to apply or remove the filter">
          <InfoIcon key="quick-filters-info" size="sm" color="primary" />
        </Tooltip>
      </TitleContainer>
      <CardsRow>
        {showSkeleton
          ? [0, 1, 2].map((index) => <LoadingSkeleton key={index} width="200px" height="96px" />)
          : familyTiles.map((tile) => (
              <FormTypeCard
                key={tile.value || 'all'}
                item={tile}
                isActive={tile.value ? tile.value === activeFamily : !activeFamily}
                onClick={handleSelectFamily}
              />
            ))}
      </CardsRow>
      {!showSkeleton && typeChips.length > 0 && (
        <ChipsRow>
          {typeChips.map((chip) => (
            <TypeChip
              key={chip.value}
              type="button"
              data-active={chip.value === activeType}
              aria-pressed={chip.value === activeType}
              onClick={() => handleSelectType(chip.value === activeType ? null : chip.value)}
            >
              {chip.label} ({chip.count})
            </TypeChip>
          ))}
        </ChipsRow>
      )}
    </TilesContainer>
  )
}
